/**
 * Seeded pseudo-random number generation.
 *
 * The game NEVER calls Math.random(). Every random decision goes through a
 * seeded Rng so that headless sim tests (tests/sim/) are reproducible: same
 * seed plus the same fixed timestep means the same granny behavior every run.
 * That determinism is what makes the AI testable at all.
 */

export class Rng {
  private state: number;

  constructor(seed = 0x9e3779b9) {
    // Avoid a zero state, which would make mulberry32 degenerate.
    this.state = seed === 0 ? 0x9e3779b9 : seed >>> 0;
  }

  /** Uniform float in [0, 1). mulberry32 - small, fast, good enough for a game. */
  next(): number {
    this.state = (this.state + 0x6d2b79f5) >>> 0;
    let t = this.state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  /** Uniform float in [min, max). */
  range(min: number, max: number): number {
    return min + this.next() * (max - min);
  }

  /** Uniform integer in [min, max] inclusive. */
  int(min: number, max: number): number {
    return Math.floor(this.range(min, max + 1));
  }

  /** True with the given probability (0-1). */
  chance(probability: number): boolean {
    return this.next() < probability;
  }

  /** Uniformly pick one element. Throws on an empty array rather than returning undefined. */
  pick<T>(items: readonly T[]): T {
    if (items.length === 0) throw new Error('Rng.pick called with an empty array');
    const value = items[this.int(0, items.length - 1)];
    // noUncheckedIndexedAccess: the index is provably in range, but narrow anyway.
    if (value === undefined) throw new Error('Rng.pick produced undefined');
    return value;
  }

  /** Weighted pick. Weights need not sum to 1. */
  pickWeighted<T>(items: readonly T[], weightOf: (item: T) => number): T {
    if (items.length === 0) throw new Error('Rng.pickWeighted called with an empty array');
    let total = 0;
    for (const item of items) total += Math.max(0, weightOf(item));
    if (total <= 0) return this.pick(items);
    let roll = this.next() * total;
    for (const item of items) {
      roll -= Math.max(0, weightOf(item));
      if (roll <= 0) return item;
    }
    return items[items.length - 1] as T;
  }

  /** Snapshot/restore, so a test can fork a deterministic stream. */
  getState(): number {
    return this.state;
  }

  setState(state: number): void {
    this.state = state >>> 0;
  }
}

/**
 * The shared gameplay Rng. Reseed at level start (levelSeed) so a replay of the
 * same level behaves identically. Tests construct their own Rng instead.
 */
export const gameRng = new Rng();
